<template>
  <SfModal
    :visible="visible"
    :title="$t('Generate the quote')"
    :narrow="true"
    @close="handleClose"
  >
    <form
      class="flex flex-col gap-4"
      @submit.stop.prevent="onFormSubmit"
    >
      <p class="text-center text-EXPONDO-grey">
        {{ $t('To generate your PDF quote, we need a few details from you.') }}
      </p>

      <VatIdInput
        :vat-number="vatId"
        :name="'vatId'"
        :should-validate-vat="false"
        @input="vatId = String($event ?? '')"
      />

      <PrimaryInput
        :model-value="companyName"
        :title="$t('Your company name')"
        :label="$t('Your company name')"
        required
        :valid="!companyNameError"
        :error-message="companyNameError"
        @update:model-value="companyName = String($event ?? '')"
      />

      <PrimaryInput
        :model-value="streetAndHouseNumber"
        :title="$t('Street name and house number')"
        :label="$t('Street name and house number')"
        required
        :valid="!streetAndHouseNumberError"
        :error-message="streetAndHouseNumberError"
        @update:model-value="streetAndHouseNumber = String($event ?? '')"
      />

      <PrimaryInput
        :model-value="city"
        :title="$t('City')"
        :label="$t('City')"
        required
        :valid="!cityError"
        :error-message="cityError"
        @update:model-value="city = String($event ?? '')"
      />

      <PrimaryInput
        :model-value="postcode"
        :title="$t('Postcode')"
        :label="$t('Postcode')"
        required
        :valid="!postcodeError"
        :error-message="postcodeError"
        @update:model-value="postcode = String($event ?? '')"
      />

      <div class="mt-2 flex flex-col gap-3">
        <CustomButton
          type="primary"
          class="w-full"
          :loading="loading"
          :disabled="loading"
          is-submit-type
        >
          {{ $t('Generate quote') }}
        </CustomButton>
        <CustomButton
          type="secondary"
          class="w-full"
          :disabled="loading"
          @click="handleClose"
        >
          {{ $t('Cancel') }}
        </CustomButton>
      </div>
    </form>
  </SfModal>
</template>

<script lang="ts">

import { useField, useForm } from 'vee-validate';
import formatVatNumberForLocale from '~~/helpers/formatVatNumberForLocale';
import type { QuoteForm } from '~~/types/components/Cart/QuoteForm';

export default defineComponent({
  name: 'CartGenerateQuoteModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit'],
  setup(props, { emit }) {

    const { countryCode } = useLocalization();

    const normalizeVatId = (value: unknown): string => {

      const rawValue = String(value ?? '').trim();

      if (!rawValue || !countryCode.value) {

        return rawValue;

      }

      return formatVatNumberForLocale(countryCode.value, rawValue);

    };

    const { handleSubmit } = useForm({
      validationSchema: {
        companyName: 'required',
        streetAndHouseNumber: 'required',
        city: 'required',
        postcode: 'required',
      },
      initialValues: {
        vatId: '',
        companyName: '',
        streetAndHouseNumber: '',
        city: '',
        postcode: '',
      },
    });

    const { value: vatId } = useField<string>('vatId');
    const { value: companyName, errorMessage: companyNameError } = useField<string>('companyName');
    const {
      value: streetAndHouseNumber,
      errorMessage: streetAndHouseNumberError,
    } = useField<string>('streetAndHouseNumber');
    const { value: city, errorMessage: cityError } = useField<string>('city');
    const { value: postcode, errorMessage: postcodeError } = useField<string>('postcode');

    const handleClose = () => {

      if (props.loading) {

        return;

      }

      emit('close');

    };

    const submitHandler = handleSubmit(() => {

      const quoteForm: QuoteForm = {
        vatId: normalizeVatId(vatId.value || ''),
        companyName: companyName.value || '',
        streetAndHouseNumber: streetAndHouseNumber.value || '',
        city: city.value || '',
        postcode: postcode.value || '',
      };

      emit('submit', quoteForm);

    });

    const onFormSubmit = (event: Event) => {

      submitHandler(event);

    };

    return {
      vatId,
      companyName,
      streetAndHouseNumber,
      city,
      postcode,
      companyNameError,
      streetAndHouseNumberError,
      cityError,
      postcodeError,
      handleClose,
      onFormSubmit,
    };

  },
});

</script>
